import React, { useEffect, useState } from "react";

import { DatasetDataApi } from "api/DatasetDataApi";
import { I, T } from "services/lang/LanguageService";
import { useGetDatasetDataInfo } from "api/api-hooks/datasets-hooks";
import { DatasetDataLagLeadRequest, UpdateDatasetData } from "api/model/requests";
import ChartInDialogButton from "components/basic/chart-in-dialog-button/ChartInDialogButton";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { ChronoUnitLegacy } from "utils/time-utils";
import {
  BooleanTypeTemplate,
  NumberTypeTemplate,
  TitleOverrideAndDescriptionTemplate,
} from "../../../templates/DataTableCellTemplates";
import { ICombinedData, ITableConfigProp } from "../common/api/interfaces/interfaces";
import DataInputOutputUpdateTable from "../update-dataset-table/DataInputOutputUpdateTable";
import {
  getDataConfigLocalFullyFilledConfig,
  getDateFromGivenConfig,
  getExtractedDataValue,
  remakeArrayIntoOtherInterface,
} from "../utils";
import "./DataInputOutputTable.scss";

const defaultEmptyItem = {
  identifier: "",
  decimalPlaces: 0,
  description: "",
  title: "",
  enum: undefined,
  data: [],
  unit: "",
  type: "NUMBER",
  editable: false,
};

const DataInputOutputTable = (props: ITableConfigProp) => {
  const allDatasetsData = props.datasets.map(ds => ({
    identifier: ds.identifier,
    overriddenTitle: ds.overriddenTitle,
    enum: ds.enum,
    type: ds.type,
    decimalPlaces: ds.decimalPlaces,
  }));

  const identifiers = props.datasets.map(ds => ds.identifier).filter(identifier => identifier);

  const config: DatasetDataLagLeadRequest = getDataConfigLocalFullyFilledConfig(props.config || {});
  const [dataQuery, setDataQuery] = useState<Array<ICombinedData>>([]);

  const [dialog, setDialog] = useState(false);
  const { data, isError, isLoading, refetch } = useGetDatasetDataInfo(identifiers, config, {
    enabled: config && identifiers && identifiers.length > 0,
    refetchInterval:
      props.config?.updateFrequency && !dialog ? props.config?.updateFrequency * 1000 : undefined,
  });

  useEffect(() => {
    if (data && !isError && !isLoading && data?.length > 0) {
      setDataQuery([...remakeArrayIntoOtherInterface(data, props.datasets)]);
    }
  }, [data]);

  const handleCloseDialog = () => {
    refetch();
    setDialog(false);
  };

  const titleColumnRender = (rowData: ICombinedData) => {
    return rowData.identifier && rowData.identifier.length > 0 ? (
      <div className="flex gap-2 align-items-center">
        <TitleOverrideAndDescriptionTemplate
          data={{
            title: rowData.title,
            overriddenTitle: rowData.overriddenTitle,
            description: rowData.description,
            identifier: rowData.identifier,
          }}
        />
        <ChartInDialogButton identifier={rowData.identifier} />
      </div>
    ) : (
      <div>{rowData.overriddenTitle ? I(rowData.overriddenTitle) : " "}</div>
    );
  };
  const valueColumnRender = (rowData: ICombinedData) => {
    if (rowData.identifier === "") {
      return "";
    }

    if (rowData.type === undefined || rowData.type.toUpperCase() === "NUMBER") {
      return NumberTypeTemplate(rowData.data, rowData.unit, rowData.decimalPlaces);
    }
    if (rowData.type.toUpperCase() === "BOOLEAN") {
      const changeBooleanValue = (val: boolean) => {
        const boolToNumber = val ? 1 : 0;

        DatasetDataApi.updateData([
          {
            identifier: rowData.identifier,
            values: {
              [`${rowData.data[0].key}`]: boolToNumber,
            },
          },
        ] as Array<UpdateDatasetData>)
          .then(() => {
            easasAlert({ summary: T("alert.datasets-updated"), severity: "success" });
            setDataQuery(oldDataQuery => {
              return oldDataQuery.map(ob => {
                if (ob.identifier === rowData.identifier) {
                  ob.data[0].value = boolToNumber;
                }
                return ob;
              });
            });
          })
          .catch(er => {
            console.error(er);
            easasAlert({ summary: T("alert.error-while-updating-datasets"), severity: "error" });
          });
      };

      return BooleanTypeTemplate(rowData.data, changeBooleanValue);
    }
    if (rowData.type.toUpperCase() === "ENUM") {
      let val = "";
      if (rowData.data && rowData.data.length > 0 && rowData.enum) {
        Object.entries(rowData.enum).forEach(([key, value]) => {
          if (value === +getExtractedDataValue(rowData.data)) {
            val = key;
          }
        });
      }
      return val || "-";
    }
    return "Unknown type";
  };
  const handleValue = () => {
    const dataQueryMap = new Map(dataQuery.map(item => [item.identifier, item]));

    const newData = allDatasetsData.map(obj => {
      if (obj.identifier === "") {
        return { ...defaultEmptyItem, overriddenTitle: obj.overriddenTitle };
      }
      const existingData = dataQueryMap.get(obj.identifier);

      return existingData
        ? {
            ...existingData,
            overriddenTitle: obj.overriddenTitle,
            type: obj.type,
            enum: obj.enum,
            decimalPlaces: obj.decimalPlaces,
          }
        : {
            identifier: obj.identifier,
            decimalPlaces: 0,
            overriddenTitle: obj.overriddenTitle,
            description: T("word.not-found"),
            title: T("word.not-found"),
            data: [],
            enum: undefined,
            unit: "Unknown",
            type: "NUMBER",
            editable: false,
          };
    });

    return newData;
  };

  return (
    <>
      {isError && (
        <div className="error-window">
          <Message severity="error" text={T("error.error-while-fetching-datasets")} />
        </div>
      )}

      {isLoading && !isError && (
        <ProgressSpinner
          style={{ display: "flex", justifyContent: "space-around", overflow: "hidden" }}
        />
      )}

      {!isError && dataQuery.length > 0 && (
        <div className="full-table flex flex-column gap-2">
          <DataTable
            width="100%"
            value={dataQuery ? handleValue() : []}
            responsiveLayout="scroll"
            scrollHeight={`${props.config?.maxHeight}px`}
            size="small"
          >
            <Column
              field="title"
              headerStyle={props.hideHeader ? { display: "none" } : undefined}
              header={T("word.dataset")}
              body={titleColumnRender}
              style={{ paddingLeft: "15px" }}
            />
            <Column
              field="unit"
              headerStyle={props.hideHeader ? { display: "none" } : undefined}
              header={T("word.value")}
              body={valueColumnRender}
              className="value-field"
            />
          </DataTable>
          {props.config?.displayNote && props.config?.note && (
            <div
              style={{ overflowWrap: "break-word" }}
              className="surface-200 p-3 border-round-lg m-2"
              dangerouslySetInnerHTML={{ __html: props.config?.note }}
            />
          )}
          {data && data.length > 0 && !props.config?.hideUpdateButton && (
            <Button
              style={{ margin: "0.3vw", width: "fit-content" }}
              label={T("action.update")}
              className="p-button-info p-button-sm"
              icon="pi pi-sync"
              onClick={() => setDialog(true)}
            />
          )}

          <Dialog
            breakpoints={{ "960px": "75vw", "640px": "100vw" }}
            style={{ width: "50vw" }}
            resizable={false}
            header={T("action.update-datasets")}
            visible={dialog}
            onHide={handleCloseDialog}
            blockScroll
          >
            <DataInputOutputUpdateTable
              titleColumnReader={titleColumnRender}
              identifiers={allDatasetsData.map(item => item.identifier)}
              firstLoadDate={getDateFromGivenConfig(props.config || {})}
              datasets={props.datasets}
              dateFrequency={
                (props.config?.dataFrequency as ChronoUnitLegacy) || ChronoUnitLegacy.MINUTES
              }
              closeDialog={handleCloseDialog}
            />
          </Dialog>
        </div>
      )}
    </>
  );
};

export default DataInputOutputTable;
